import { Component, ViewEncapsulation, Input, ChangeDetectionStrategy } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  SegmentConnectionTypes,
  ProvidersExcludedFromDuration
} from '@shared/flight-segment-connection/entities/segment-types';
import { PlaceSelectors } from '@app/entities/place';
import { FlightDurationPipe } from '@app/pipes/flight-duration.pipe';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { TranslatePipe } from '@app/translate/TranslatePipe';
import { TimeFormatPipe } from '@app/pipes/time-format.pipe';
import { Flight } from '@app/entities/flight-results';
import { AirValidationFlightsSector } from '@app/entities/air-validation.response';
import { TOTAL_DURATION_NULL } from '@shared/flight-itinerary/constants/total-duration';

@Component({
  selector: 'app-flight-segment-connection',
  templateUrl: './flight-segment-connection.component.html',
  styleUrls: ['./flight-segment-connection.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FlightSegmentConnectionComponent {

  @Input() hasScales: boolean;
  @Input() isScaleElement: boolean;
  @Input() provider: string;
  @Input() notBorderRadius: boolean;

  @Input() isConnectionInDifferentAirport: boolean;
  @Input() numberOfStops: number;
  @Input() totalDuration: string;
  @Input() technicalStopAirport: string;
  @Input() technicalStopTime: string;
  @Input() flight: Flight | AirValidationFlightsSector;
  @Input() nextFlight: Flight | AirValidationFlightsSector;

  public segmenTypeTotal: SegmentConnectionTypes = SegmentConnectionTypes.TOTAL;
  public segmenTypeTechnicalStop: SegmentConnectionTypes = SegmentConnectionTypes.TECHNICAL_STOP;
  public segmenTypeScale: SegmentConnectionTypes = SegmentConnectionTypes.SCALE;

  private placeCriteria: string = PlaceSelectors.City;

  constructor(
    private flightDurationPipe: FlightDurationPipe,
    private placeNamePipe: PlaceNamePipe,
    private translatePipe: TranslatePipe,
    private timeFormatPipe: TimeFormatPipe) { }

  get isExcludedFromDuration(): boolean {
    return Object.values(
      ProvidersExcludedFromDuration).includes(
        this.provider as ProvidersExcludedFromDuration) || !this.hasDuration;
  }

  get hasDuration(): boolean {
    const regexOnlyZeroTime = new RegExp(TOTAL_DURATION_NULL);
    return !!this.totalDuration && !regexOnlyZeroTime.test(this.totalDuration);
  }

  get hasTechnicalStops(): boolean {
    return this.numberOfStops > 0;
  }

  get flightSegmentTotalMessage(): string {
    return this.translatePipe.transform('FLIGHT_RESULTS_MODULE.ITINERARY_LIST_SECTION.DURATION_TOTAL', {
      time: this.timeFormatPipe.transform(this.totalDuration)
    });
  }

  get segmentTechnicalStopMessage$(): Observable<string> {
    return this.placeNamePipe.transform(this.technicalStopAirport, this.placeCriteria).pipe(map(city =>
      this.translatePipe.transform('FLIGHT_RESULTS_MODULE.ITINERARY_LIST_SECTION.TECHNICAL_STOP', {
        time: this.timeFormatPipe.transform(this.technicalStopTime),
        location: city
      })
    ));
  }

  get segmentScaleMessage$(): Observable<string> {
    const segmentDuration = this.flightDurationPipe.transform(this.flight.ArrivalDate, this.nextFlight.DepartureDate);
    const translateLabel = this.isConnectionInDifferentAirport ? 'CONNECTION_WITH_AIRPORT_CHANGE' : 'CONNECTION';
    return this.placeNamePipe.transform(this.flight.ArrivalAirport, this.placeCriteria).pipe(map(city =>
      this.translatePipe.transform(`FLIGHT_RESULTS_MODULE.ITINERARY_LIST_SECTION.${translateLabel}`, {
        time: segmentDuration,
        location: city
      })
    ));
  }

}
